import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  searchWeaponOptions,
  type ModerationDetail,
  type ProposalInput,
} from '../../lib/moderationApi';
import { inputClass } from './ModerationUi';
import {
  ReviewedTextInput,
  VerificationCheckbox,
} from './ProposalVerificationField';

export type ProposalEditorState = ProposalInput & {
  fallbackConfirmed: boolean;
  weaponConfirmed: boolean;
  zeroDistanceConfirmed: boolean;
  weaponVehicleFrag: boolean | null;
  verified: Record<ProposalField, boolean>;
};

export type ProposalField = Exclude<keyof ProposalInput, 'k_is_solo' | 'v_is_solo'>;

const proposalFields: ProposalField[] = [
  'k_name',
  'k_squad',
  'v_name',
  'v_squad',
  'w_class',
  'distance',
  'tk',
];

const unverifiedFields = (): Record<ProposalField, boolean> => ({
  k_name: false,
  k_squad: false,
  v_name: false,
  v_squad: false,
  w_class: false,
  distance: false,
  tk: false,
});

export const initialProposalState = (
  detail: ModerationDetail,
  prefillClaimedWeapon = false
): ProposalEditorState => ({
  k_squad: detail.claimed_killer_squad,
  k_is_solo: detail.claimed_killer_is_solo,
  k_name: detail.claimed_killer_name,
  w_class: prefillClaimedWeapon ? detail.claimed_weapon_or_method : '',
  distance: detail.claimed_distance ?? 0,
  v_name: detail.claimed_victim_name,
  v_squad: detail.claimed_victim_squad,
  v_is_solo: detail.claimed_victim_is_solo,
  tk: 0,
  fallbackConfirmed: false,
  weaponConfirmed: false,
  zeroDistanceConfirmed:
    detail.claimed_distance !== null && detail.claimed_distance !== 0,
  weaponVehicleFrag: prefillClaimedWeapon ? null : false,
  verified: unverifiedFields(),
});

export const proposalReady = (
  value: ProposalEditorState,
  requireFieldVerification = false
): boolean =>
  Boolean(
    value.k_name.trim() &&
      (value.k_is_solo ? !value.k_squad.trim() : Boolean(value.k_squad.trim())) &&
      value.v_name.trim() &&
      (value.weaponVehicleFrag
        ? !value.v_is_solo && !value.v_squad.trim()
        : value.v_is_solo
          ? !value.v_squad.trim()
          : Boolean(value.v_squad.trim())) &&
      value.distance >= 0 &&
      (value.w_class.trim() ? value.weaponConfirmed : value.fallbackConfirmed) &&
      (value.distance !== 0 || value.zeroDistanceConfirmed) &&
      (!requireFieldVerification ||
        proposalFields.every((field) => value.verified[field]))
  );

export const proposalInput = (value: ProposalEditorState): ProposalInput => ({
  k_squad: value.k_squad.trim(),
  k_is_solo: value.k_is_solo,
  k_name: value.k_name.trim(),
  w_class: value.w_class.trim(),
  distance: value.distance,
  v_name: value.v_name.trim(),
  v_squad: value.v_squad.trim(),
  v_is_solo: value.v_is_solo,
  tk: value.tk,
});

const booleanFlag = (value: 0 | 1 | undefined): string =>
  value === undefined ? '—' : value === 1 ? 'True' : 'False';

export function ProposalEditor({
  value,
  onChange,
  requireFieldVerification = false,
}: {
  value: ProposalEditorState;
  onChange: (value: ProposalEditorState) => void;
  requireFieldVerification?: boolean;
}): JSX.Element {
  const trimmedWeapon = value.w_class.trim();
  const weapons = useQuery({
    queryKey: ['moderation', 'weapons', value.w_class],
    queryFn: () => searchWeaponOptions(value.w_class),
    enabled: Boolean(trimmedWeapon),
  });
  const selected = useMemo(
    () =>
      weapons.data?.results.find(
        (option) =>
          option.className.toLocaleLowerCase() ===
          trimmedWeapon.toLocaleLowerCase()
      ),
    [trimmedWeapon, weapons.data]
  );
  const classification = trimmedWeapon
    ? selected
    : { className: 'binocular', type: 'Rifle', wk: 0 as const, ak: 0 as const };
  const weaponVehicleFrag = selected
    ? selected.wk === 1
    : trimmedWeapon
      ? null
      : false;

  useEffect(() => {
    const confirmed = Boolean(selected);
    const canonicalClassName = selected?.className ?? value.w_class;
    const vehicleVictimChanged = Boolean(
      weaponVehicleFrag && (value.v_squad || value.v_is_solo)
    );
    if (
      value.weaponConfirmed !== confirmed ||
      value.w_class !== canonicalClassName ||
      value.weaponVehicleFrag !== weaponVehicleFrag ||
      vehicleVictimChanged
    ) {
      onChange({
        ...value,
        w_class: canonicalClassName,
        weaponConfirmed: confirmed,
        weaponVehicleFrag,
        v_squad: weaponVehicleFrag ? '' : value.v_squad,
        v_is_solo: weaponVehicleFrag ? false : value.v_is_solo,
        verified: vehicleVictimChanged
          ? { ...value.verified, v_squad: false }
          : value.verified,
      });
    }
  }, [onChange, selected, value, weaponVehicleFrag]);

  const setFlag = <Key extends 'fallbackConfirmed' | 'zeroDistanceConfirmed'>(
    key: Key,
    next: ProposalEditorState[Key]
  ) => onChange({ ...value, [key]: next });
  const setField = <Key extends ProposalField>(
    key: Key,
    next: ProposalEditorState[Key],
    extra: Partial<ProposalEditorState> = {}
  ) =>
    onChange({
      ...value,
      ...extra,
      [key]: next,
      verified: { ...value.verified, [key]: false },
    });
  const setVerified = (field: ProposalField, checked: boolean) =>
    onChange({
      ...value,
      verified: { ...value.verified, [field]: checked },
    });
  const setSolo = (
    soloField: 'k_is_solo' | 'v_is_solo',
    squadField: 'k_squad' | 'v_squad',
    checked: boolean
  ) =>
    onChange({
      ...value,
      [soloField]: checked,
      [squadField]: checked ? '' : value[squadField],
      verified: { ...value.verified, [squadField]: false },
    });
  const soloControl = (
    label: string,
    checked: boolean,
    disabled: boolean,
    onCheckedChange: (checked: boolean) => void
  ) => (
    <label className={`flex items-center gap-2 text-sm ${disabled ? 'text-muted-foreground' : ''}`}>
      <input
        type="checkbox"
        aria-label={`Одиночка: ${label}`}
        checked={checked}
        disabled={disabled}
        onChange={(event) => onCheckedChange(event.target.checked)}
      />
      <span>Одиночка</span>
    </label>
  );
  const verification = (field: ProposalField, label: string) =>
    requireFieldVerification ? (
      <VerificationCheckbox
        field={field}
        label={label}
        checked={value.verified[field]}
        onChange={setVerified}
      />
    ) : null;

  const weaponState = !trimmedWeapon
    ? 'fallback'
    : weapons.isPending || weapons.isFetching
      ? 'loading'
      : weapons.isError
        ? 'error'
        : selected
          ? 'recognized'
          : 'unknown';
  const weaponStateClass =
    weaponState === 'recognized'
      ? 'border-emerald-500 bg-emerald-50/60 dark:bg-emerald-950/20'
      : weaponState === 'unknown' || weaponState === 'error'
        ? 'border-destructive bg-destructive/5'
        : '';
  const killerSquadError =
    !value.k_is_solo && !value.k_squad.trim()
      ? 'Укажите тег отряда или отметьте «Одиночка».'
      : undefined;
  const victimSquadError = classification?.wk === 1
    ? value.v_is_solo
      ? 'Класс техники не может быть отмечен как одиночка.'
      : value.v_squad.trim()
        ? 'У класса техники не может быть отряда.'
        : undefined
    : !value.v_is_solo && !value.v_squad.trim()
      ? 'Укажите тег отряда или отметьте «Одиночка».'
      : undefined;

  return (
    <fieldset className="space-y-4 rounded-md border border-border p-4">
      <legend className="px-2 text-sm font-semibold">Предложение корректировки</legend>
      {requireFieldVerification ? (
        <p className="text-sm text-muted-foreground">
          Проверь правильность введённых пользователем данных или исправь их самостоятельно.
        </p>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <ReviewedTextInput
          field="k_name"
          label="Автор фрага"
          value={value.k_name}
          help="Должен быть только позывной, без тега, например для [DWI]Krupa надо указать Krupa."
          requireVerification={requireFieldVerification}
          verified={value.verified.k_name}
          onValueChange={(next) => setField('k_name', next)}
          onVerificationChange={setVerified}
        />
        <ReviewedTextInput
          field="k_squad"
          label="Отряд автора фрага"
          value={value.k_squad}
          help="Должен быть только тег отряда, без квадратных скобок, например для [DWI]Krupa надо указать DWI."
          error={killerSquadError}
          required={!value.k_is_solo}
          disabled={value.k_is_solo}
          secondaryControl={soloControl(
            'автор фрага',
            value.k_is_solo,
            false,
            (checked) => setSolo('k_is_solo', 'k_squad', checked)
          )}
          requireVerification={requireFieldVerification}
          verified={value.verified.k_squad}
          onValueChange={(next) => setField('k_squad', next)}
          onVerificationChange={setVerified}
        />
        <ReviewedTextInput
          field="v_name"
          label="Жертва"
          value={value.v_name}
          help="Должен быть только позывной, без тега, например для [DWI]Krupa надо указать Krupa."
          requireVerification={requireFieldVerification}
          verified={value.verified.v_name}
          onValueChange={(next) => setField('v_name', next)}
          onVerificationChange={setVerified}
        />
        <ReviewedTextInput
          field="v_squad"
          label="Отряд жертвы"
          value={value.v_squad}
          help="Должен быть только тег отряда, без квадратных скобок, например для [DWI]Krupa надо указать DWI."
          error={victimSquadError}
          required={classification?.wk !== 1 && !value.v_is_solo}
          disabled={classification?.wk === 1 || value.v_is_solo}
          secondaryControl={soloControl(
            'жертва',
            value.v_is_solo,
            classification?.wk === 1,
            (checked) => setSolo('v_is_solo', 'v_squad', checked)
          )}
          requireVerification={requireFieldVerification}
          verified={value.verified.v_squad}
          onValueChange={(next) => setField('v_squad', next)}
          onVerificationChange={setVerified}
        />
        <div className="space-y-2">
          <label className="block space-y-1">
            <span className="text-sm">Класс оружия</span>
            <input
              className={`${inputClass} ${weaponStateClass}`}
              aria-label="Класс оружия"
              list="moderation-weapons"
              placeholder="Пусто — canonical fallback"
              value={value.w_class}
              aria-describedby="moderation-weapon-status"
              onChange={(e) =>
                setField('w_class', e.target.value, {
                  fallbackConfirmed: false,
                  weaponConfirmed: false,
                  weaponVehicleFrag: null,
                })
              }
            />
            <span id="moderation-weapon-status" className="block text-xs" aria-live="polite">
              {weaponState === 'recognized' ? `Класс распознан: ${selected?.className}.` : null}
              {weaponState === 'unknown' ? 'Класс не найден. Нужна ручная корректировка.' : null}
              {weaponState === 'loading' ? 'Проверяем класс…' : null}
              {weaponState === 'error' ? 'Не удалось проверить класс. Повторите попытку.' : null}
            </span>
          </label>
          <datalist id="moderation-weapons">
            {weapons.data?.results.map((option) => (
              <option key={option.className} value={option.className}>
                {option.type}
              </option>
            ))}
          </datalist>
          {verification('w_class', 'Класс оружия')}
        </div>
        <div className="space-y-2">
          <label className="block space-y-1">
            <span className="text-sm">Дистанция, м</span>
            <input
              className={inputClass}
              aria-label="Дистанция, м"
              type="number"
              min={0}
              value={value.distance}
              onChange={(e) => {
                const distance = Number(e.target.value);
                setField('distance', distance, {
                  zeroDistanceConfirmed: distance !== 0,
                });
              }}
              required
            />
          </label>
          {verification('distance', 'Дистанция, м')}
        </div>
        <div className="space-y-2">
          <label className="block space-y-1">
            <span className="text-sm">Тип события</span>
            <select aria-label="Тип события" className={inputClass} value={value.tk} onChange={(e) => setField('tk', Number(e.target.value) as 0 | 1)}>
              <option value={0}>Фраг</option>
              <option value={1}>Team kill</option>
            </select>
          </label>
          {verification('tk', 'Тип события')}
        </div>
        <div className="rounded-md bg-muted/50 p-3 text-sm" aria-live="polite">
          <p>type_w: <strong>{classification?.type ?? 'Выберите точный класс'}</strong></p>
          <p>Vehicle frag: <strong>{booleanFlag(classification?.wk)}</strong></p>
          <p>Artillery frag: <strong>{booleanFlag(classification?.ak)}</strong></p>
        </div>
      </div>
      {!value.w_class.trim() ? (
        <label className="flex items-start gap-2 text-sm">
          <input
            className="mt-1"
            type="checkbox"
            checked={value.fallbackConfirmed}
            onChange={(e) => setFlag('fallbackConfirmed', e.target.checked)}
          />
          <span>
            Подтверждаю fallback <strong>binocular / Rifle</strong>, Vehicle frag=False,
            Artillery frag=False и учёт в пехотной статистике.
          </span>
        </label>
      ) : null}
      {value.distance === 0 ? (
        <label className="flex items-start gap-2 text-sm">
          <input
            className="mt-1"
            type="checkbox"
            checked={value.zeroDistanceConfirmed}
            onChange={(e) => setFlag('zeroDistanceConfirmed', e.target.checked)}
          />
          <span>Подтверждаю сохранение неизвестной дистанции как 0 метров.</span>
        </label>
      ) : null}
    </fieldset>
  );
}
